import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API } from '@/utils/API';
import { readZipExport } from '@/hooks/documents/useDocumentZipExport';

/**
 * Final Documents data source, shared by the study and submission surfaces.
 *
 * The payload deliberately carries no version identifier and no release
 * identifier — the customer sees documents and the date they received them,
 * never the plumbing. See ReleasedDocumentBlueprint for the three rules.
 */
export interface ReleasedDocument {
  /**
   * A per-ROW handle, not a document id. The space lists one row per released
   * (document, version) pair, so one document can appear twice — Current and
   * Superseded — and the two rows have different ids. Treat it as an opaque
   * download token: pass it back untouched, never parse or compare it to a
   * document id.
   */
  id: string;
  filename: string;
  released_at: string;
  /**
   * OXX-1066 added 'withdrawn', and it OVERRIDES the other two — a withdrawn
   * document may also be superseded, but being pulled back is the stronger
   * fact and the one that explains why it can no longer be downloaded.
   *
   * This union is the only compile-time guard on the set. The value crosses an
   * API boundary, so a new state added server-side will not error here on its
   * own; widening this deliberately is what forces every `switch`/ternary over
   * it to be revisited. OXX-1076 exists because that did not happen last time
   * and a withdrawn document rendered as "Current".
   */
  status: 'current' | 'superseded' | 'withdrawn';
  /** Set only when status is 'withdrawn'. The date the document was pulled back. */
  withdrawn_at?: string | null;
  /**
   * OXX-1135 (customer item R-09). The site a document was released to.
   * `site_code` is the site's INV number (the display_id of its originating
   * New Site/Investigator filing), not the site's own free-text display_id,
   * which is nil for submission-created sites.
   */
  site_code: string | null;
  site_name: string | null;
  pi_name: string | null;
  /**
   * Why the three above may be blank. 'study' is a genuinely study-wide
   * release; 'site' is one restricted to specific sites that produced no
   * usable label (an unnamed site with no PI and no kept filing). Render
   * "Study" ONLY for 'study' — inferring it from three nulls would tell the
   * customer a site-restricted document went out study-wide.
   *
   * Optional: absent from any OneVerse build predating the field.
   */
  site_scope?: 'study' | 'site';
  uploaded_by: string;
  /**
   * OXX-1133. The Associated Submission/Reference recorded on the release that
   * delivered this document: a study-level submission's display id, or the bare
   * string 'Other' when Ops chose Other and typed a note Univo-side.
   *
   * Optional AND nullable: null for every release sealed before the field
   * existed, and absent from any OneVerse build predating it. The column must
   * render a placeholder for both, never an empty cell.
   */
  associated_submission?: string | null;
}

export type ReleasedDocumentsScope = 'study' | 'submission' | 'site';

const LIST_PATHS: Record<ReleasedDocumentsScope, (id: string) => string> = {
  study: id => `/studies/${id}/final_documents`,
  submission: id => `/submissions/${id}/final_documents`,
  site: id => `/sites/${id}/final_documents`,
};

const ZIP_PATHS: Record<ReleasedDocumentsScope, (id: string) => string> = {
  study: id => `/studies/${id}/final_documents_zip`,
  submission: id => `/submissions/${id}/final_documents_zip`,
  site: id => `/sites/${id}/final_documents_zip`,
};

const ZIP_FILENAME = 'final_documents.zip';

/**
 * Downloads a customer-selected set of rows as one ZIP.
 *
 * Rows are sent by their own ids rather than by document id, so selecting a
 * Superseded row returns the bytes the customer was operating under at the
 * time. The submitted-documents ZIP resolves by document and always returns
 * current content, which would be the wrong answer here.
 *
 * The server is fail-whole: if any selected row can no longer be served (a
 * version revoked since the page loaded), nothing comes back rather than a
 * silently incomplete ZIP.
 */
export function useReleasedDocumentsDownload(
  scope: ReleasedDocumentsScope,
  id: string
) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [zipExportId, setZipExportId] = useState<string | null>(null);

  const download = useCallback(
    async (documentIds: string[]) => {
      if (documentIds.length === 0) return;

      setIsDownloading(true);
      setDownloadError(null);
      setZipExportId(null);
      try {
        const response = await API.authenticated.fetch(ZIP_PATHS[scope](id), {
          method: 'POST',
          body: JSON.stringify({ ids: documentIds }),
        });

        // OV-1405: a large selection is prepared in the background. The
        // table then shows its progress and downloads it when it is ready.
        const zipExport = await readZipExport(response);
        if (zipExport) {
          setZipExportId(zipExport.id);
          return;
        }

        if (!response.ok) {
          setDownloadError(
            response.status === 404
              ? 'Some of those documents are no longer available. Refresh and try again.'
              : 'We couldn’t prepare that download. Please try again.'
          );
          return;
        }

        const blobUrl = URL.createObjectURL(await response.blob());
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = ZIP_FILENAME;
        link.click();
        URL.revokeObjectURL(blobUrl);
      } catch {
        setDownloadError(
          'We couldn’t prepare that download. Please try again.'
        );
      } finally {
        setIsDownloading(false);
      }
    },
    [scope, id]
  );

  return { download, isDownloading, downloadError, zipExportId };
}

export function useReleasedDocuments({
  scope,
  id,
}: {
  scope: ReleasedDocumentsScope;
  id: string;
}) {
  const query = useQuery({
    queryKey: ['released-documents', scope, id],
    queryFn: async (): Promise<ReleasedDocument[]> => {
      const response = await API.authenticated.fetch(LIST_PATHS[scope](id));
      if (!response.ok) {
        throw new Error('Failed to load final documents');
      }
      const body = (await response.json()) as { documents: ReleasedDocument[] };
      return body.documents ?? [];
    },
    enabled: Boolean(id),
  });

  return {
    documents: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
